// components/OrderComplaints.js - Complaints inside the order screen (the one opened from
// Search): the compact complaint-line block under Uploads, the registration pop-up that wraps
// the guided ComplaintLineEditor, and the merged line timeline in the right column.
//
// The server only sends the `complaints` block to reviewers, supervisors and admins; the
// screen draws these components only when it is there.
import React, { useEffect, useMemo, useState } from 'react';
import { SectionHeader } from './shared';
import ComplaintLineEditor from './ComplaintLineEditor';
import { useLang } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import api, { getErrorMessage } from '../utils/api';
import {
  EVENT_DOT_COLOR, STATUS_BADGE, formatMoment, orderSummary, orderTimeline, valueLabel,
} from '../utils/complaints';

const isFinal = (line) => line.status === 'solved' || line.status === 'cancelled';

// ── Counter ───────────────────────────────────────────────────────────────────
function CounterBadge({ lines }) {
  const { t } = useLang();
  const { solved, total } = orderSummary(lines);
  if (total === 0) return null;
  return (
    <span className={`badge ${solved === total ? 'badge-success' : 'badge-warning'}`} style={{ marginLeft: 8 }}>
      {t('complaints.summary').replace('{solved}', solved).replace('{total}', total)}
    </span>
  );
}

// ── Lines block ───────────────────────────────────────────────────────────────
export function ComplaintLinesBlock({ block, taxonomy, onAdd, onEdit, onChanged }) {
  const { t } = useLang();
  const { lines, attachments, can_edit: canEdit, complaint } = block;
  const canAdd = canEdit && !!taxonomy;
  return (
    <div style={{ marginTop: 20 }}>
      <SectionHeader
        title={<>{t('complaints.lines')} ({lines.length})<CounterBadge lines={lines} /></>}
        action={canAdd ? (
          <button className="btn btn-secondary btn-sm" onClick={onAdd}>+ {t('complaints.addline')}</button>
        ) : null} />
      {lines.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '4px 0' }}>{t('complaints.nolines')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {lines.map((line) => (
            <ComplaintLineRow key={line.id} line={line} complaintId={complaint?.id} taxonomy={taxonomy}
              canEdit={canEdit} showCost={block.access === 'full'}
              attachments={attachments.filter((a) => a.line_id === line.id)}
              onEdit={onEdit} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

// One line on one or two rows: status and category with the actions on the first, the
// verdict (solution, causer, cost bearer) and service data on the second.
function ComplaintLineRow({ line, complaintId, taxonomy, canEdit, showCost, attachments, onEdit, onChanged }) {
  const { t } = useLang();
  const toast = useToast();
  const [panel, setPanel] = useState(null); // 'service' | 'cancel'
  const [reason, setReason] = useState('');
  const [service, setService] = useState({ service_partner: '', service_case_ref: '' });
  const [busy, setBusy] = useState(false);
  const cancelled = line.status === 'cancelled';
  const v = (field) => valueLabel(taxonomy, field, line[field]);

  const category = [v('main_complaint'), v('detail')].filter(Boolean).join(': ');
  const what = [v('article_type'), v('element_type')].filter(Boolean).join(' · ');
  const meta = [
    line.solution && [t('complaints.field.solution'), v('solution')],
    line.causer && [t('complaints.field.causer'), v('causer')],
    showCost && line.cost_bearer && [t('complaints.field.cost_bearer'), line.cost_bearer],
    (line.service_partner || line.service_case_ref)
      && [t('oc.service'), [line.service_partner, line.service_case_ref].filter(Boolean).join(' · ')],
  ].filter(Boolean);

  const run = async (request, done) => {
    setBusy(true);
    try {
      await request();
      toast.success(done);
      setPanel(null);
      setReason('');
      await onChanged();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    } finally { setBusy(false); }
  };
  const url = `/api/complaints/${complaintId}/lines/${line.id}`;
  // Reopening a closed line goes straight into the answers that need changing.
  const reopen = async () => {
    const ok = await run(() => api.post(`${url}/reopen`, {}),
      cancelled ? t('complaints.line.uncancelled') : t('complaints.line.reopened'));
    if (ok && !cancelled) onEdit({ ...line, status: 'incomplete_verdict' });
  };
  const cancel = () => run(() => api.post(`${url}/cancel`, { reason: reason.trim() }), t('complaints.line.cancelled'));
  const saveService = () => run(
    () => api.patch(url, { service_partner: service.service_partner || null, service_case_ref: service.service_case_ref.trim() || null }),
    t('complaints.saved'),
  );
  const openService = () => {
    setService({ service_partner: line.service_partner || '', service_case_ref: line.service_case_ref || '' });
    setPanel('service');
  };

  return (
    <div className={`cl-row${cancelled ? ' cancelled' : ''}`}>
      <div className="cl-main">
        <strong>{t('complaints.line')} {line.line_no}</strong>
        <span className={`badge ${STATUS_BADGE[line.status] || 'badge-secondary'}`}>{t(`complaints.status.${line.status}`, line.status)}</span>
        <span className="cl-cat" title={[category, what].filter(Boolean).join(' · ')}>
          {category || <span style={{ color: 'var(--text-muted)' }}>{t('complaints.nodetail')}</span>}
          {what && <span style={{ color: 'var(--text-muted)' }}> · {what}</span>}
        </span>
        {canEdit && !panel && (
          <div className="cl-actions">
            {!isFinal(line) && taxonomy && (
              <button className="btn btn-secondary btn-sm" onClick={() => onEdit(line)}>{t('complaints.continue')}</button>
            )}
            {!cancelled && (
              <button className="btn btn-ghost btn-sm" onClick={openService}>✎ {t('oc.service')}</button>
            )}
            {(line.status === 'solved' || cancelled) && (
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={reopen}>
                ↺ {cancelled ? t('complaints.uncancel') : t('complaints.reopen')}
              </button>
            )}
            {!cancelled && (
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }} onClick={() => setPanel('cancel')}>
                {t('complaints.cancelline')}
              </button>
            )}
          </div>
        )}
      </div>

      {(meta.length > 0 || attachments.length > 0 || (cancelled && line.cancelled_reason)) && (
        <div className="cl-meta">
          {meta.map(([label, value]) => (
            <span key={label}><span className="k">{label}:</span> {value}</span>
          ))}
          {cancelled && line.cancelled_reason && <span><span className="k">{t('complaints.cancelreason')}:</span> {line.cancelled_reason}</span>}
          {attachments.map((a) => (
            <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>📎 {a.filename}</a>
          ))}
        </div>
      )}

      {panel === 'service' && (
        <div className="cl-panel">
          <select className="input-field" aria-label={t('complaints.partner')} value={service.service_partner}
            onChange={(e) => setService((s) => ({ ...s, service_partner: e.target.value }))}>
            <option value="">{t('complaints.partner')}</option>
            {(taxonomy?.servicePartners || []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input className="input-field" aria-label={t('complaints.caseref')} placeholder={t('complaints.caseref')}
            value={service.service_case_ref} maxLength={200}
            onChange={(e) => setService((s) => ({ ...s, service_case_ref: e.target.value }))} />
          <button className="btn btn-secondary btn-sm" onClick={() => setPanel(null)}>{t('common.cancel')}</button>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={saveService}>{t('common.save')}</button>
        </div>
      )}

      {panel === 'cancel' && (
        <div className="cl-panel">
          <input className="input-field" aria-label={t('complaints.cancel.reason')} placeholder={t('complaints.cancel.reason')}
            value={reason} autoFocus onChange={(e) => setReason(e.target.value)} />
          <button className="btn btn-secondary btn-sm" onClick={() => { setPanel(null); setReason(''); }}>{t('common.cancel')}</button>
          <button className="btn btn-sm btn-danger" disabled={busy || !reason.trim()} onClick={cancel}>{t('complaints.cancelline')}</button>
        </div>
      )}
    </div>
  );
}

// ── Registration pop-up ───────────────────────────────────────────────────────
// A bottom sheet on phones and narrow tablets, a centred dialog from 600px up (the shared
// .overlay / .modal behaviour). The guided editor inside is the same one the dossier uses.
// The dossier for the order is created on the first save, not when the pop-up opens.
export function ComplaintSheet({ order, block, line, taxonomy, onClose, onSaved }) {
  const { t } = useLang();
  const title = line ? `${t('complaints.line')} ${line.line_no}` : t('oc.register');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const resolveComplaintId = async () => {
    try {
      const { data } = await api.post('/api/complaints', { order_number: order.order_number, order_id: order.id });
      return data.complaint.id;
    } catch (err) {
      // Someone registered the dossier in the meantime: continue on that one.
      const existing = err?.response?.status === 409 && err.response.data?.existing_complaint_id;
      if (existing) return existing;
      throw err;
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal" style={{ maxWidth: 720, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn btn-ghost btn-icon" aria-label={t('oc.close')} onClick={onClose}>✕</button>
        </div>
        <div className="form-hint" style={{ margin: '2px 0 14px' }}>
          {[order.order_number, order.product_name].filter(Boolean).join(' · ')}
        </div>
        <ComplaintLineEditor complaintId={block.complaint?.id || null} resolveComplaintId={resolveComplaintId}
          line={line} taxonomy={taxonomy} onSaved={onSaved} onClose={onClose} />
      </div>
    </div>
  );
}

// ── Timeline ──────────────────────────────────────────────────────────────────
// All line events of the order in one list, newest first like the activity log below it,
// each starting with its line ("Line 2 · Verdict saved"). The chips narrow it to one line.
export function ComplaintTimeline({ events, lines }) {
  const { t } = useLang();
  const [lineId, setLineId] = useState(null);
  const items = useMemo(() => orderTimeline(events, lines, lineId).reverse(), [events, lines, lineId]);
  const sorted = useMemo(() => [...lines].sort((a, b) => (a.line_no || 0) - (b.line_no || 0)), [lines]);

  return (
    <div className="card">
      <SectionHeader title={`${t('complaints.timeline')} (${orderTimeline(events, lines).length})`} />
      {sorted.length > 1 && (
        <div role="group" aria-label={t('oc.timeline.filter')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <button className={`btn btn-sm ${!lineId ? 'btn-primary' : 'btn-secondary'}`} aria-pressed={!lineId}
            onClick={() => setLineId(null)}>{t('oc.timeline.all')}</button>
          {sorted.map((l) => (
            <button key={l.id} className={`btn btn-sm ${lineId === l.id ? 'btn-primary' : 'btn-secondary'}`}
              aria-pressed={lineId === l.id} onClick={() => setLineId(l.id)}>
              {t('complaints.line')} {l.line_no}
            </button>
          ))}
        </div>
      )}
      {items.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{t('complaints.noevents')}</div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {items.map((e) => (
            <div key={e.id} className="activity-item">
              <div className="activity-dot" style={EVENT_DOT_COLOR[e.type] ? { background: EVENT_DOT_COLOR[e.type] } : undefined} />
              <div style={{ minWidth: 0 }}>
                <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                  {t('complaints.line')} {e.line_no} · {t(`complaints.event.${e.type}`, e.type.replace(/_/g, ' '))}{e.n ? ` (${e.n})` : ''}
                </span>
                {e.data?.reason && <span style={{ color: 'var(--text-muted)' }}> · {e.data.reason}</span>}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {e.actor ? `${e.actor} · ` : ''}{formatMoment(e.at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
